/**
 * The sidebar's Activity badge (utils/activityBadge.js): the inbox from the live poll it shares with the notifications
 * bell (hooks/useInbox.js), and the AI tools' sessions polled every minute while the page is visible; opening Activity
 * (`onActivityPage`) marks it visited, which clears the sessions part.
 */
import { useCallback, useEffect, useState } from 'react';
import useInbox from './useInbox';
import { getMyActivity } from '../services/SandboxService';
import { activityNewCount, lastActivityVisit, markActivityVisited } from '../utils/activityBadge';

const POLL_MS = 60000;

export default function useActivityBadge({ enabled = true, onActivityPage = false } = {}) {
  const { inbox } = useInbox({ enabled });
  const [sessions, setSessions] = useState([]);
  const [since, setSince] = useState(lastActivityVisit);

  const load = useCallback(async () => {
    try {
      const next = (await getMyActivity({ limit: 50 }))?.sessions || [];
      // The same answer re-renders nothing.
      setSessions((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
    } catch {
      // keep the last answer
    }
  }, []);

  useEffect(() => {
    if (!enabled) { return undefined; }
    load();
    const timer = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') { load(); }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, load]);

  useEffect(() => {
    if (!onActivityPage) { return undefined; }
    const visit = () => { const now = Date.now(); markActivityVisited(now); setSince(now); };
    visit();
    return visit; // leaving Activity counts as having seen it too
  }, [onActivityPage]);

  return enabled ? activityNewCount({ inbox, sessions, since }) : 0;
}
